import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
  ProfileAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { ModalPath } from "@/constants/Navigation";
import { ModalTestID, ProfileTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import usePushNotifications from "@/hooks/usePushNotifications";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetFullProfileQuery,
  useUpdateUserNotificationSettingsMutation,
} from "@/store/api/user";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { NotificationSettingsSection } from "@/ui/components/notificationSettings";
import { getChangesForMixpanel } from "@/ui/components/notificationSettings/NotificationSettingsSection";
import ModalFooter from "@/ui/composed/modal/ModalFooter";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import { FAIconName } from "@/ui/core";
import { VStack } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import { LayoutToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";
import { getNotificationSettings } from "@/utils/user.util";

const baseTestID = ProfileTestID.EditNotificationSettings;

const Modal = () => {
  const { track, trackPageView } = useAnalytics();
  const { externalId: appUserId } = useAppUserSelector();

  useEffect(() => {
    trackPageView(ProfileAnalyticsPageView.EditNotificationSettings);
  }, [trackPageView]);

  const { registerForPushNotifications } = usePushNotifications();
  const { data: unmodifiedProfile } = useGetFullProfileQuery();
  const notifications = getNotificationSettings(unmodifiedProfile);
  const [notificationSettings, setNotificationSettings] =
    useState<NotificationSettings>(getNotificationSettings(unmodifiedProfile));

  useEffect(() => {
    setNotificationSettings(getNotificationSettings(unmodifiedProfile));
  }, [unmodifiedProfile]);

  const [updateUserNotificationSettings, { isLoading }] =
    useUpdateUserNotificationSettingsMutation();

  const onModalDismiss = () => {
    track(ProfileAnalyticsEvent.DismissEditNotificationSettings, {
      [AnalyticsFieldName.Component]: ModalPath.EditNotificationSettings,
      [AnalyticsFieldName.Source]: ModalPath.EditNotificationSettings,
    });
    router.dismiss();
  };

  const handleCancelPress = () => {
    track(ProfileAnalyticsEvent.CancelEditNotificationSettings, {
      [AnalyticsFieldName.Component]: ModalPath.EditNotificationSettings,
      [AnalyticsFieldName.Source]: ModalPath.EditNotificationSettings,
    });
    router.dismiss();
  };

  const handleSaveChanges = () => {
    if (isNil(unmodifiedProfile)) {
      return;
    }

    if (!isWeb() && notificationSettings.receivePush) {
      void registerForPushNotifications();
    }

    // user permissions for viewability/edit-ability of each field is handled by
    // each respective component in NotificationSettingsSection
    // ultimately a full payload with all expected fields must be constructed here for the PUT request
    // and non-editable fields will simply retain their previous values
    const payload: UpdateNotificationSettingsBody = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveNotificationEmails: notificationSettings.receiveEmails,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveNotificationPush: notificationSettings.receivePush,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      receiveNotificationSms: notificationSettings.receiveSms,
    };

    track(ProfileAnalyticsEvent.SaveChangesEditNotificationSettings, {
      [AnalyticsFieldName.Component]: ModalPath.EditNotificationSettings,
      [AnalyticsFieldName.Source]: ModalPath.EditNotificationSettings,
      ...getChangesForMixpanel({ notifications, payload }),
    });

    void updateUserNotificationSettings({
      body: payload,
      isPatient: unmodifiedProfile.isPatient,
      userExternalId: appUserId,
    })
      .unwrap()
      .then(() => router.dismiss());
  };

  return (
    <ModalLayout
      isPaddedHorizontally
      renderFooter={() => {
        return (
          <ModalFooter
            isLoading={isLoading}
            leftButtonType="cancel"
            onLeftButtonPress={handleCancelPress}
            onRightButtonPress={handleSaveChanges}
            rightButtonLabel="Save changes"
            rightButtonLeftIcon={FAIconName.Check}
            testID={baseTestID}
          />
        );
      }}
      renderHeader={() => {
        return (
          <ModalTitleHeader
            navigationType="close"
            onDismiss={onModalDismiss}
            testID={joinTestIDs(
              ModalTestID.TitleHeader,
              ModalTestID.EditNotificationSettings,
            )}
            title="Edit notification settings"
          />
        );
      }}
    >
      <VStack
        paddingBottom={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingTop={LayoutToken.ContentPaddingHorizontal}
      >
        <NotificationSettingsSection
          onChange={setNotificationSettings}
          receiveEmails={notifications.receiveEmails}
          receivePush={notifications.receivePush}
          receiveSms={notifications.receiveSms}
        />
      </VStack>
    </ModalLayout>
  );
};

export default Modal;
